import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { images } from '../../components/shared/assets';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import { usePageContent, usePackages, useSiteSettings } from '../../hooks/useContentful';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../components/shared/FilterBar';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { PageToggleButton } from '../../components/shared/PageToggleButton/PageToggleButton';
import './PackagesPage.css';

const PackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedDestination, setSelectedDestination] = useState<string | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('packages');
  const { data: packagesData } = usePackages();
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const allPackages = packagesData || [];
  const siteSettings = siteSettingsData?.[0]?.fields as any;

  // Filter packages based on selected filters
  const filteredPackages = allPackages.filter(pkg => {
    const packageFields = pkg.fields as any;
    const tags = packageFields.tags || [];
    
    // Filter by destination
    if (selectedDestination) {
      const hasDestination = tags.some((tag: string) => 
        tag.toLowerCase().includes(selectedDestination.toLowerCase())
      );
      if (!hasDestination) return false;
    }
    
    // Filter by type
    if (selectedType) {
      const hasType = tags.some((tag: string) => 
        tag.toLowerCase().includes(selectedType.toLowerCase()) ||
        packageFields.category?.toLowerCase().includes(selectedType.toLowerCase())
      );
      if (!hasType) return false;
    }
    
    return true;
  });
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const packages = filteredPackages.slice(startIndex, endIndex);

  const handleViewMore = (packageId: string) => {
    navigate(`/packages/${packageId}`);
  };

  return (
    <div className="packages-page">
      {/* Navigation */}
      <SmartNavigation 
        defaultVariant="transparent"
        scrolledVariant="transparent"
        autoSwitchOnScroll={false}
        forceVariant="transparent"
      />

      {/* Hero Section */}
      <section className="packages-hero">
        <div 
          className="packages-hero-background"
          style={{ backgroundImage: `url('/assets/aa60b9577306018199f9f7e1dcd4ec989ae77573.png')` }}
        ></div>
        <div className="packages-hero-content">
          <h1>The Perfect South American Adventure</h1>
          <p>
            Posuere lacinia purus faucibus urna pretium facilisis nulla in donec. Integer ornare amet donec morbi est cursus viverra ullamcorper egestas. Turpis in non amet dictumst quisque. Eu mi ut pretium tincidunt nec tincidunt. Cursus mauris condimentum dui tristique in.
          </p>
        </div>
      </section>

      {/* Packages Section with Filters and Grid */}
      <section className="packages-section">
        <div className="packages-wrapper">
          {/* Filter Section */}
          <div className="packages-filters">
            <FilterBar
              className="packages-filter-bar packages-filter-override"
              dropdowns={[
                {
                  id: 'destination',
                  type: 'destination',
                  level: 'brand',
                  options: DESTINATION_OPTIONS,
                  selectedValue: selectedDestination,
                  onSelect: (value: string) => setSelectedDestination(value === selectedDestination ? undefined : value)
                },
                {
                  id: 'type',
                  type: 'type',
                  level: 'brand',
                  options: TYPE_OPTIONS,
                  selectedValue: selectedType,
                  onSelect: (value: string) => setSelectedType(value === selectedType ? undefined : value)
                }
              ]}
              alignment="left"
            />
            <HandDrawnButton
              text={pageContent?.combineOffersText || t('pages/packages:filters.combineOffers')}
              variant="brand"
              color="awasi"
              animation="stroke-to-fill"
              onClick={() => navigate('/packages/combined')}
              className="combine-offers-btn"
            />
          </div>

          {/* Packages Grid */}
          <div className="packages-container">
          {packages.length > 0 ? (
            packages.map((pkg) => {
              const packageFields = pkg.fields as any;
              const tags = packageFields.tags || [];
              const imageUrl = packageFields.featuredImage?.fields?.file?.url;
              
              return (
                <div key={pkg.sys.id} className="package-card">
                  <div className="package-image">
                    <img src={imageUrl ? `https:${imageUrl}` : images.galleryMain} alt={packageFields.title} />
                    <div className="package-tags">
                      {tags.map((tag: string, index: number) => (
                        <span key={index} className="tag">
                          <ContentfulText 
                            content={tag} 
                            fallback={tag}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="package-content">
                    <h3>
                      <ContentfulText 
                        content={packageFields.title} 
                        fallback="Package Title"
                      />
                    </h3>
                    <div className="package-links">
                      <HandDrawnButton
                        text={packageFields.buttonText || pageContent?.defaultButtonText || t('pages/packages:readMore')}
                        variant="brand"
                        color="awasi"
                        animation="stroke-to-fill"
                        size="normal"
                        onClick={() => handleViewMore(packageFields.slug || pkg.sys.id)}
                        className="package-view-more-btn"
                      />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            // Real packages with Figma images - showing 6 cards matching Figma design
            <>
              {/* Card 1 */}
              <div className="package-card">
                <div className="package-image">
                  <img src="/assets/a4c6f14c191075a6114eee0361c1491931dad6db.png" alt="Combine 2 or 3 of our lodges" />
                </div>
                <div className="package-content">
                  <h3>Combine 2 or 3 of our lodges for the perfect South American Adventure</h3>
                  <div className="package-links">
                    <HandDrawnButton
                      text="VIEW MORE"
                      variant="brand"
                      color="awasi"
                      animation="stroke-to-fill"
                      size="normal"
                      onClick={() => handleViewMore('combine-lodges')}
                      className="package-view-more-btn"
                    />
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="package-card">
                <div className="package-image">
                  <img src="/assets/5bd0ef3d88344c93558950d902a99017e8cffc12.png" alt="Stay Longer Package" />
                </div>
                <div className="package-content">
                  <h3>Stay Longer at Awasi</h3>
                  <div className="package-links">
                    <HandDrawnButton
                      text="VIEW MORE"
                      variant="brand"
                      color="awasi"
                      animation="stroke-to-fill"
                      size="normal"
                      onClick={() => handleViewMore('stay-longer')}
                      className="package-view-more-btn"
                    />
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="package-card">
                <div className="package-image">
                  <img src="/assets/a3b69810ad729ddf4967f6860984b388f32b8d5c.png" alt="RE-ENERGIZE Package" />
                </div>
                <div className="package-content">
                  <h3>RE-ENERGIZE: Wellness and Adventure in Atacama</h3>
                  <div className="package-links">
                    <HandDrawnButton
                      text="VIEW MORE"
                      variant="brand"
                      color="awasi"
                      animation="stroke-to-fill"
                      size="normal"
                      onClick={() => handleViewMore('re-energize')}
                      className="package-view-more-btn"
                    />
                  </div>
                </div>
              </div>

              {/* Card 4 */}
              <div className="package-card">
                <div className="package-image">
                  <img src="/assets/e4b1294f7fdb8bb50ef6b56bc1b1317911f04905.png" alt="Special Wellness Package" />
                </div>
                <div className="package-content">
                  <h3>Exclusive Wellness Experience in the Desert</h3>
                  <div className="package-links">
                    <HandDrawnButton
                      text="VIEW MORE"
                      variant="brand"
                      color="awasi"
                      animation="stroke-to-fill"
                      size="normal"
                      onClick={() => handleViewMore('wellness-desert')}
                      className="package-view-more-btn"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        </div>
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="packages-pagination">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
            <PageToggleButton
              key={pageNum}
              number={pageNum}
              isActive={currentPage === pageNum}
              onClick={() => setCurrentPage(pageNum)}
              color="awasi"
              className={`packages-page-button ${currentPage === pageNum ? 'packages-page-active' : ''}`}
            />
          ))}
        </div>
      )}

      {/* Instagram Section - Same as brand home page */}
      <InstagramSection posts={[
        { src: images.film13, alt: "Instagram 1" },
        { src: images.filmVideo1, alt: "Instagram 2", isVideo: true },
        { src: images.film14, alt: "Instagram 3" },
        { src: images.film15, alt: "Instagram 4" }
      ]} />

      {/* Footer - Same as brand home page */}
      <Footer variant="default" />
    </div>
  );
};

export default PackagesPage;